import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { View, StyleSheet } from 'react-native';
import { useTranslation } from 'react-i18next';
import {
  NAMESPACED_VALUE_MAX,
  foldDiacritics,
  slugifyTagValue,
} from '@heraion/shared';
import { Button, Dialog, TextField, useGymChip } from '../index';

import { locationLabel } from './locationLabel';
import { getKnownLocations, useEntryHistory } from './useEntryHistory';
import type { LoggerDispatch } from './state/loggerDraft';
import { useTheme } from './theme';

// EQ-1 slice D — the gym chip as the logger mounts it.
//
// Thin by design: `useGymChip` owns the control and the tray, this owns only
// what is mobile's to know — which places history has seen, how a slug reads
// in the user's language, and how a new one gets named.
//
// ⭐ Since D2b the add and swap pickers mount this same hook (Antenore: "deve
// imperativamente utilizzare la stessa primitiva") — see LoggerModals, which
// calls it once per picker. Three surfaces, one control. ⚠️ Each mount calls
// the hook separately on purpose: the tray's open/closed state is per-call, so
// one shared result would open all the trays together. The VALUE is shared
// regardless, because every mount reads the same draft place and dispatches
// the same action.
//
// ⛔ The place changes nothing about the logging. It is a grouping that ranks
// what surfaces first when you go looking for an exercise or a machine — see
// equipment-model.md §9.12.

export function useLoggerGymChip({
  value,
  dispatch,
  defaultLocation,
  patchProfile,
  notify,
}: {
  value: string | null;
  dispatch: LoggerDispatch;
  /**
   * EQ-1-GYM-DEFAULT — the elected default place (`Profile.defaultLocation`) and
   * the door to change it, threaded from the host exactly as the machine tray's
   * `equipmentDefault`/`patchProfile` are. All three OPTIONAL: a mount with no
   * profile door (the cold-cache render test, any host that cannot persist)
   * shows no ★ and behaves exactly as before.
   */
  defaultLocation?: string | undefined;
  patchProfile?: (patch: { defaultLocation?: string }) => Promise<void>;
  notify?: (title: string, body: string) => void;
}): { trigger: ReactNode; tray: ReactNode } {
  const { t } = useTranslation();
  const theme = useTheme();
  const [naming, setNaming] = useState(false);
  const [typed, setTyped] = useState('');

  // EQ-1-GYM-DEFAULT — the optimistic overlay for the ★, inline because the
  // field is a single SCALAR: _useProfileListOverlay is list-shaped (a set of
  // rows to add/drop), and force-fitting one value through it would be the
  // reinvention that hook exists to prevent. The CONTRACT is the same, narrowed
  // to a scalar: write optimistically, roll back on failure, and send empty as
  // undefined so the API's setIfPresent REMOVEs the attribute rather than
  // leaving a stale one. `null` = no pending overlay; the object holds the
  // optimistic value, which may itself be `undefined` (a demotion).
  const [pendingDefault, setPendingDefault] = useState<{
    value: string | undefined;
  } | null>(null);
  const effectiveDefault = pendingDefault ? pendingDefault.value : defaultLocation;
  // Drop the overlay once the server snapshot catches up — otherwise a later
  // change from elsewhere (another device, the web) would stay masked by a
  // pending value that is no longer pending.
  useEffect(() => {
    setPendingDefault((p) => (p && p.value === defaultLocation ? null : p));
  }, [defaultLocation]);

  const setDefaultLocation = (next: string | undefined) => {
    if (!patchProfile) return;
    setPendingDefault({ value: next });
    void patchProfile({ defaultLocation: next }).catch(() => {
      // Back to what the server is known to hold: a write that failed must not
      // leave the athlete looking at a default nobody stored.
      setPendingDefault(null);
      notify?.(
        t('gym.pinErrorTitle', { defaultValue: 'Not saved' }),
        t('gym.pinErrorBody', { defaultValue: 'Your default place is unchanged.' }),
      );
    });
  };

  // ⚠️ The history window is filled ASYNCHRONOUSLY, so this must recompute when
  // the answer lands. It did not: the memo keyed on `value` alone, and on a
  // FRESH workout (nobody warms the cache first, unlike the plan path) it ran
  // against a cold cache and never ran again — Antenore, 2026-08-20: *"la mia
  // palestra LGF Le Mont non esiste, esiste solo nel programma"*. Literally
  // true: his gym was in the window, and nothing ever asked for it a second
  // time. Proven with a failing render test before this line existed.
  const { ready } = useEntryHistory('');

  // History only, most recently used first — so the usual place is one tap.
  // ⛔ No app-invented entries: he refused ready-made "Home"/"Gym" on the
  // device. The unnamed default is offered by the chip itself, not from here.
  const places = useMemo(() => {
    const seen = new Set<string>();
    const out: { value: string; label: string }[] = [];
    // ⛔ Read, not just depended on. `ready` is what makes this recompute when
    // the window lands, and a dependency that the body never touches is one an
    // autofix removes later — which would restore the bug silently. It also
    // says something true: with a cold window there is nothing to list yet
    // except the place already chosen this session.
    if (!ready) {
      return value ? [{ value, label: locationLabel(value) }] : [];
    }
    const push = (v: string) => {
      if (seen.has(v)) return;
      seen.add(v);
      out.push({ value: v, label: locationLabel(v) });
    };
    for (const p of getKnownLocations()) push(p.value);
    // A place chosen this session that history has not seen yet — the same
    // reason mintedInstances exists for machines: it lives only in the draft
    // until the workout is saved, and it must not vanish from its own list.
    if (value) push(value);
    return out;
  }, [value, ready]);

  // Fold accents BEFORE slugifying: slugifyTagValue deletes what it cannot
  // map, so "Genève" would otherwise be stored as `genve`.
  const slug = slugifyTagValue(foldDiacritics(typed)).slice(
    0,
    NAMESPACED_VALUE_MAX,
  );

  const commitName = () => {
    // An input of only punctuation slugifies to nothing, and the button that
    // would write it is disabled — so this guard is the keyboard's "done"
    // path, not a silent failure the athlete can reach by tapping Save.
    if (slug.length === 0) return;
    setNaming(false);
    setTyped('');
    dispatch({ type: 'set_location', location: slug });
  };

  const { trigger, tray } = useGymChip({
    theme,
    value,
    places,
    onChoose: (v) => dispatch({ type: 'set_location', location: v }),
    onClear: () => dispatch({ type: 'set_location', location: null }),
    onCreate: () => setNaming(true),
    labels: {
      idle: t('gym.setPlace', { defaultValue: 'Where?' }),
      question: t('gym.whichPlace', {
        defaultValue: 'Where are you training?',
      }),
      create: t('gym.newPlace', { defaultValue: 'Add place' }),
      fallback: t('gym.fallback', { defaultValue: 'Default' }),
      a11y: t('gym.chipA11y', { defaultValue: 'Training place' }),
      footnote: t('gym.footnote', {
        defaultValue: 'Sorts what you see first. Never shared with a coach.',
      }),
    },
    // Only when the host can persist — no door, no ★ (see GymChip's `pin`). The
    // label/hint follow the state, built here with `t` because the primitive
    // holds no i18n, exactly as the machine tray builds its own.
    ...(patchProfile
      ? {
          pin: {
            pinned: effectiveDefault,
            onToggle: (place: string) =>
              setDefaultLocation(
                effectiveDefault === place ? undefined : place,
              ),
            accessibilityLabel: (placeLabel: string, active: boolean) =>
              active
                ? t('gym.unpinPlace', {
                    place: placeLabel,
                    defaultValue: 'Remove {{place}} as your default place',
                  })
                : t('gym.pinPlace', {
                    place: placeLabel,
                    defaultValue: 'Set {{place}} as your default place',
                  }),
            accessibilityHint: (active: boolean) =>
              active
                ? t('gym.unpinHint', {
                    defaultValue: 'Forgets your default place.',
                  })
                : t('gym.pinHint', {
                    defaultValue:
                      'Remembers this as the place you usually train at.',
                  }),
          },
        }
      : {}),
  });

  // The naming dialog rides with the tray: it is opened from inside it, and
  // both belong to the region under the header rather than to the chip cell.
  const trayWithDialog = (
    <>
      {tray}
      <Dialog
        theme={theme}
        visible={naming}
        onRequestClose={() => setNaming(false)}
        title={t('gym.namePlaceTitle', { defaultValue: 'Name this place' })}
      >
        <TextField
          theme={theme}
          value={typed}
          onChangeText={setTyped}
          // The title names the field visually; a reader landing on the input
          // itself would otherwise have only the placeholder, which vanishes
          // on the first keystroke.
          accessibilityLabel={t('gym.namePlaceTitle', {
            defaultValue: 'Name this place',
          })}
          placeholder={t('gym.namePlaceHint', { defaultValue: 'Place name' })}
          autoFocus
          autoCorrect={false}
          maxLength={NAMESPACED_VALUE_MAX}
          onSubmitEditing={commitName}
          returnKeyType="done"
        />
        <View style={styles.actions}>
          <Button
            theme={theme}
            variant="ghost"
            label={t('mobile.logger.cancel')}
            onPress={() => setNaming(false)}
          />
          <Button
            theme={theme}
            variant="primary"
            // Disabled rather than accepting-then-doing-nothing: typing only
            // punctuation used to close the dialog in silence, which reads as
            // the app losing the input.
            disabled={slug.length === 0}
            label={t('gym.namePlaceSave', { defaultValue: 'Use this place' })}
            onPress={commitName}
          />
        </View>
      </Dialog>
    </>
  );

  return { trigger, tray: trayWithDialog };
}

const styles = StyleSheet.create({
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: 12,
  },
});
